class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.requestId = 0;
    this.composing = false;
    this.resetButton?.addEventListener('click', this.clear.bind(this));
    const schedule = FoxTheme.utils.debounce(() => this.onChange(), 300);
    this.input.addEventListener('input', () => {
      this.cancelRequest();
      this.resultsTarget?.replaceChildren();
      this.input.setAttribute('aria-expanded', 'false');
      if (!this.composing) schedule();
    });
    this.input.addEventListener('compositionstart', () => {
      this.composing = true;
      this.cancelRequest();
    });
    this.input.addEventListener('compositionend', () => {
      this.composing = false;
      schedule();
    });
    this.input.addEventListener('focus', () => {
      if (this.closest('.template-search')) document.body.classList.add('predictive-search-open');
      if (this.getQuery()) this.onChange();
    });
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.cancelRequest();
        this.searchContent?.classList.add('hidden');
        this.input.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('predictive-search-open');
      }
    });
    // 候補はTabキーで移動できる通常のリンクとして扱います。
    ['role', 'aria-owns', 'aria-haspopup', 'aria-autocomplete', 'aria-expanded'].forEach(name => this.input.removeAttribute(name));
    if (this.resultsTarget) {
      this.resultsTarget.removeAttribute('role');
      this.resultsTarget.setAttribute('aria-live', 'polite');
      this.input.setAttribute('aria-controls', this.resultsTarget.id);
    }
    if (!this.querySelector('input[name="type"]')) {
      const field = document.createElement('input');
      field.type = 'hidden';
      field.name = 'type';
      field.value = 'product';
      this.append(field);
    }
    if (this.closest('.template-search')) document.addEventListener('click', this.handleClickOutside.bind(this));
  }
  get input() { return this.querySelector('input[type="search"]'); }
  get resetButton() { return this.querySelector('button[type="reset"]'); }
  get resultsTarget() { return this.querySelector('[id^="PredictiveSearchResults-"]'); }
  getQuery() { return this.input.value.trim(); }
  cancelRequest() {
    this.requestId += 1;
    this.controller?.abort();
    this.removeAttribute('loading');
    this.resetButton?.classList.remove('btn--loading');
  }
  clear(event = null) {
    event?.preventDefault();
    this.cancelRequest();
    this.input.value = '';
    this.resultsTarget?.replaceChildren();
    if (this.searchRecommendationEmpty) this.searchContent?.classList.add('hidden');
    this.removeAttribute('results');
    this.removeAttribute('error');
    this.input.focus();
  }
  setupURL() {
    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(root + 'search', window.location.origin);
    url.searchParams.set('q', this.getQuery());
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('section_id', 'live-product-search');
    // 非公開テーマのプレビューを維持します。
    const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  }
  async onChange() {
    if (this.composing) return;
    if (!this.getQuery()) { this.clear(); return; }
    this.cancelRequest();
    const requestId = this.requestId;
    const query = this.getQuery();
    const url = this.setupURL().toString();
    const controller = new AbortController();
    this.controller = controller;
    this.setAttribute('loading', 'true');
    this.removeAttribute('error');
    this.resetButton?.classList.add('btn--loading');
    try {
      let html = this.cachedMap.get(url);
      if (html === undefined) {
        const response = await fetch(url, {signal: controller.signal, credentials: 'same-origin'});
        if (!response.ok) throw new Error('Search failed: ' + response.status);
        html = await response.text();
      }
      if (requestId !== this.requestId || query !== this.getQuery() || this.composing) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('[data-live-product-results]');
      if (!content || !this.resultsTarget) throw new Error('Candidate section not found. Check the preview theme.');
      this.resultsTarget.innerHTML = content.innerHTML;
      this.searchContent?.classList.remove('hidden');
      this.setAttribute('results', 'true');
      if (!this.cachedMap.has(url)) {
        if (this.cachedMap.size >= 30) this.cachedMap.delete(this.cachedMap.keys().next().value);
        this.cachedMap.set(url, html);
      }
    } catch (error) {
      if (error.name === 'AbortError' || requestId !== this.requestId) return;
      this.setAttribute('error', 'Failed to load data');
      if (this.resultsTarget) {
        this.resultsTarget.replaceChildren();
        const message = document.createElement('p');
        message.textContent = '候補を取得できませんでした。Enterキーで通常検索してください。';
        this.resultsTarget.append(message);
        this.searchContent?.classList.remove('hidden');
      }
      console.error(error);
    } finally {
      if (requestId === this.requestId) {
        this.removeAttribute('loading');
        this.resetButton?.classList.remove('btn--loading');
      }
    }
  }
  handleClickOutside(event) {
    if (!this.contains(event.target)) document.body.classList.remove('predictive-search-open');
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() { super(); }
  get requiresBodyAppended() { return false; }
  get input() { return this.querySelector('input[type="search"]'); }
  get focusElement() { return this.querySelector('input[type="search"]'); }
}
customElements.define('search-drawer', SearchDrawer);